<template>
  <div
    :class="['iai-carousel', variant === 'primary' ? 'theme-primary' : 'theme-secondary', { 'is-loading': isLoading }]"
  >
    <Carousel
      v-bind="$attrs"
      :pt="carouselPt"
      :circular="props.circular"
      :autoplayInterval="props.autoplayInterval"
      :showNavigators="!props.hideNavButtons"
      :showIndicators="!props.hideIndicators"
      @vue:Mounted="isLoading = false"
    >
      <template #item="itemProps">
        <slot name="item" v-bind="itemProps"></slot>
      </template>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import Carousel, { CarouselPassThroughOptions } from "primevue/carousel";
import { computed, ref } from "vue";

interface Props {
  hideNavButtons?: boolean;
  hideIndicators?: boolean;
  variant?: "primary" | "secondary";
  indicatorsPosition?: "inside" | "outside";
  circular?: boolean;
  autoplayInterval?: number;
}
const isLoading = ref(true);
const props = withDefaults(defineProps<Props>(), {
  hideNavButtons: false,
  hideIndicators: false,
  variant: "primary",
  indicatorsPosition: "outside",
  circular: true,
  autoplayInterval: 10000,
});

const carouselPt = computed(() => {
  const basePt: CarouselPassThroughOptions = {};

  const indicatorsClasses = [
    "carousel-indicators",
    props.indicatorsPosition === "inside" ? "indicators-inside" : "indicators-outside",
  ];

  basePt.indicators = { class: indicatorsClasses };

  if (props.hideNavButtons) {
    basePt.root = { class: ["full-width-content"] };
  }

  return basePt;
});
</script>

<style lang="scss">
.is-loading {
  .p-carousel-items-container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    .p-carousel-item {
      grid-column: 1;
      grid-row: 1;
    }
  }

  .p-carousel-indicator {
    display: none;
  }
}

.p-link {
  &:focus {
    box-shadow: none !important;
  }
}

.iai-carousel:not(.is-loading) {
  position: relative;

  .full-width-content {
    .p-carousel-container {
      .p-carousel-items-content {
        width: 100%;

        .p-carousel-items-container {
          width: 100%;
        }
      }
    }
  }

  .carousel-indicators {
    display: flex !important;
    justify-content: center !important;

    &.indicators-inside {
      position: absolute;
      bottom: 0.75rem;
      left: 0;
      right: 0;
      z-index: 10;
    }

    &.indicators-outside {
      margin-top: 0.5rem;
    }
  }

  &.theme-primary {
    .p-carousel .p-carousel-indicators {
      .p-carousel-indicator {
        margin: 0 0.25rem;

        button {
          width: 0.75rem !important;
          height: 0.75rem !important;
          border-radius: 50%;
          transition: all 0.3s ease;
          background-color: var(--color-theme-4) !important;

          &:hover {
            background-color: var(--color-theme-4b) !important;
          }
        }

        &.p-highlight {
          button {
            width: 2rem !important;
            border-radius: 0.75rem;
            background-color: var(--color-theme-1) !important;

            &:hover {
              background-color: var(--color-dark-blue-50) !important;
            }
          }
        }
      }
    }
  }

  &.theme-secondary {
    .p-carousel .p-carousel-indicators {
      .p-carousel-indicator {
        margin: 0 0.25rem;

        button {
          width: 0.5rem !important;
          height: 0.5rem !important;
          border-radius: 50%;
          transition: all 0.3s ease;
          background-color: var(--color-theme-4) !important;

          &:hover {
            background-color: var(--color-theme-4b) !important;
          }
        }

        &.p-highlight {
          button {
            width: 2rem !important;
            border-radius: 0.75rem;
            background-color: var(--color-theme-2) !important;

            &:hover {
              background-color: var(--color-theme-2b) !important;
            }
          }
        }
      }
    }
  }
}
</style>
